export type AudiencePersonaId =
  | "genre-enthusiast"
  | "casual-reader"
  | "literary-critic"
  | "young-adult-reader"
  | "book-club-member"
  | "custom";

export type AudiencePersonaStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed";

export type AudienceRunStatus =
  | "running"
  | "completed"
  | "partial"
  | "failed";

export type AudienceDropOffPoint = {
  chapterPath: string;
  reason: string;
};

export type AudiencePersonaResult = {
  overallScore: number;
  hookScore: number;
  emotionScore: number;
  clarityScore: number;
  summaryQuote: string;
  strengths: string[];
  areasForImprovement: string[];
  dropOffPoints: AudienceDropOffPoint[];
  revisionPriorities: string[];
};

export type AudienceAggregate = {
  selectedPersonaCount: number;
  completedPersonaCount: number;
  failedPersonaCount: number;
  averageScores: {
    overallScore: number;
    hookScore: number;
    emotionScore: number;
    clarityScore: number;
  };
  dominantRevisionPriorities: string[];
};

export type AudiencePersonaDefinition = {
  id: AudiencePersonaId;
  title: string;
  emoji: string;
  demographics: string;
  readingProfile: string;
  taste: string;
  promptLens: string;
};

export type CustomPersonaData = {
  demographics: string;
  readingProfile: string;
  taste: string;
  promptLens: string;
};

export const AUDIENCE_PERSONAS: AudiencePersonaDefinition[] = [
  {
    id: "genre-enthusiast",
    title: "Genre Enthusiast",
    emoji: "📚",
    demographics: "25-34, reads 40+ books/year",
    readingProfile: "Deeply invested in genre conventions and payoff.",
    taste: "Loves strong world-building and complex plots.",
    promptLens:
      "Prioritize genre promise, world-building quality, payoff structure, and whether the manuscript delivers the pleasures expected by committed genre readers.",
  },
  {
    id: "casual-reader",
    title: "Casual Reader",
    emoji: "☕",
    demographics: "30-45, reads 5-10 books/year",
    readingProfile: "Buys books for momentum, accessibility, and emotional pull.",
    taste: "Prefers accessible prose and compelling characters.",
    promptLens:
      "Prioritize readability, momentum, character attachment, and whether the story feels easy to stay with after a long day.",
  },
  {
    id: "literary-critic",
    title: "Literary Critic",
    emoji: "🎓",
    demographics: "35-55, professional reviewer",
    readingProfile: "Reads analytically with a craft and originality lens.",
    taste: "Focuses on craft, originality, and thematic depth.",
    promptLens:
      "Prioritize prose control, originality, thematic coherence, image systems, and whether the manuscript earns its meaning rather than stating it.",
  },
  {
    id: "young-adult-reader",
    title: "Young Adult Reader",
    emoji: "🎧",
    demographics: "16-22, reads across genres",
    readingProfile: "Sensitive to pace, relatability, and emotional immediacy.",
    taste: "Values fast pacing, relatable characters, and social themes.",
    promptLens:
      "Prioritize pacing, emotional immediacy, character relatability, and whether scenes feel current, vivid, and worth talking about with peers.",
  },
  {
    id: "book-club-member",
    title: "Book Club Member",
    emoji: "🗣️",
    demographics: "28-50, reads for discussion",
    readingProfile: "Looks for memorable choices, dilemmas, and debate value.",
    taste: "Enjoys thought-provoking themes and discussion-worthy endings.",
    promptLens:
      "Prioritize discussion hooks, moral ambiguity, thematic tension, and whether the ending leaves readers with rich questions to debate.",
  },
];

export const AUDIENCE_PERSONA_IDS = AUDIENCE_PERSONAS.map((persona) => persona.id);

export const DEFAULT_AUDIENCE_PERSONA_IDS: AudiencePersonaId[] = [
  "genre-enthusiast",
  "casual-reader",
];

const PERSONA_ID_SET = new Set<string>(AUDIENCE_PERSONA_IDS);

export const getAudiencePersona = (personaId: string) =>
  AUDIENCE_PERSONAS.find((persona) => persona.id === personaId) ?? null;

export const normalizeAudiencePersonaIds = (
  personaIds?: string[],
  includeCustom = false,
): AudiencePersonaId[] => {
  const inputIds = (personaIds ?? [])
    .filter((value): value is string => typeof value === "string")
    .map((value) => value.trim());

  const inputSet = new Set(inputIds);

  // Get valid built-in persona IDs
  const normalized = AUDIENCE_PERSONA_IDS.filter((personaId) =>
    inputSet.has(personaId) && PERSONA_ID_SET.has(personaId),
  );

  // Include legacy "custom" if explicitly requested and present in input
  if (includeCustom && inputSet.has("custom")) {
    return [...normalized, "custom"] as AudiencePersonaId[];
  }

  // Include new-style custom persona IDs (custom:convexId)
  const customIds = inputIds.filter((id) => id.startsWith("custom:"));
  if (customIds.length > 0) {
    return [...normalized, ...customIds] as AudiencePersonaId[];
  }

  return normalized;
};

export const normalizeChapterPaths = (chapterPaths?: string[]) =>
  Array.from(
    new Set(
      (chapterPaths ?? [])
        .filter((path): path is string => typeof path === "string")
        .map((path) => path.trim())
        .filter((path) => path.length > 0),
    ),
  ).sort((a, b) => a.localeCompare(b));

export const clampAudienceScore = (value: unknown) => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return 0;
  }
  return Math.max(0, Math.min(100, Math.round(value)));
};

const toJsonCandidate = (raw: string) => {
  const fenced = raw.match(/```json\s*([\s\S]*?)```/i);
  if (fenced?.[1]) return fenced[1].trim();

  const firstBrace = raw.indexOf("{");
  const lastBrace = raw.lastIndexOf("}");
  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
    return raw.slice(firstBrace, lastBrace + 1);
  }
  return raw;
};

const normalizeTextArray = (value: unknown, limit: number) =>
  Array.isArray(value)
    ? value
        .filter((item) => typeof item === "string")
        .map((item) => item.trim())
        .filter(Boolean)
        .slice(0, limit)
    : [];

export const parseAudiencePersonaResult = (raw: string): AudiencePersonaResult => {
  let parsed: any = {};
  try {
    parsed = JSON.parse(toJsonCandidate(raw));
  } catch (_error) {
    parsed = {};
  }

  const dropOffPoints = Array.isArray(parsed?.dropOffPoints)
    ? parsed.dropOffPoints
        .map((item: any) => ({
          chapterPath:
            typeof item?.chapterPath === "string" && item.chapterPath.trim()
              ? item.chapterPath.trim()
              : "global",
          reason: typeof item?.reason === "string" ? item.reason.trim() : "",
        }))
        .filter((item: AudienceDropOffPoint) => item.reason.length > 0)
        .slice(0, 5)
    : [];

  return {
    overallScore: clampAudienceScore(parsed?.overallScore),
    hookScore: clampAudienceScore(parsed?.hookScore),
    emotionScore: clampAudienceScore(parsed?.emotionScore),
    clarityScore: clampAudienceScore(parsed?.clarityScore),
    summaryQuote:
      typeof parsed?.summaryQuote === "string" && parsed.summaryQuote.trim()
        ? parsed.summaryQuote.trim()
        : "No summary quote generated.",
    strengths: normalizeTextArray(parsed?.strengths, 5),
    areasForImprovement: normalizeTextArray(parsed?.areasForImprovement, 5),
    dropOffPoints,
    revisionPriorities: normalizeTextArray(parsed?.revisionPriorities, 5),
  };
};

export const resolveAudienceRunStatus = (
  personaStatuses: Record<string, AudiencePersonaStatus>,
): AudienceRunStatus => {
  const statuses = Object.values(personaStatuses);
  if (statuses.length === 0) return "failed";
  if (statuses.some((status) => status === "running" || status === "pending")) {
    return "running";
  }
  if (statuses.every((status) => status === "completed")) {
    return "completed";
  }

  const completedCount = statuses.filter((status) => status === "completed").length;
  const failedCount = statuses.filter((status) => status === "failed").length;

  if (completedCount > 0 && failedCount > 0) {
    return "partial";
  }

  return "failed";
};

export const computeAudienceAggregate = (
  personaResults: Record<string, AudiencePersonaResult>,
  personaStatuses: Record<string, AudiencePersonaStatus>,
  selectedPersonaIds: AudiencePersonaId[],
): AudienceAggregate => {
  const completedIds = selectedPersonaIds.filter(
    (personaId) => personaStatuses[personaId] === "completed" && personaResults[personaId],
  );
  const failedPersonaCount = selectedPersonaIds.filter(
    (personaId) => personaStatuses[personaId] === "failed",
  ).length;

  const totals = completedIds.reduce(
    (acc, personaId) => {
      const result = personaResults[personaId];
      acc.overallScore += result.overallScore;
      acc.hookScore += result.hookScore;
      acc.emotionScore += result.emotionScore;
      acc.clarityScore += result.clarityScore;
      return acc;
    },
    { overallScore: 0, hookScore: 0, emotionScore: 0, clarityScore: 0 },
  );

  const count = completedIds.length || 1;
  const priorityCounts = new Map<string, number>();
  for (const personaId of completedIds) {
    for (const priority of personaResults[personaId].revisionPriorities) {
      priorityCounts.set(priority, (priorityCounts.get(priority) ?? 0) + 1);
    }
  }

  const dominantRevisionPriorities = Array.from(priorityCounts.entries())
    .sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      return a[0].localeCompare(b[0]);
    })
    .slice(0, 5)
    .map(([priority]) => priority);

  return {
    selectedPersonaCount: selectedPersonaIds.length,
    completedPersonaCount: completedIds.length,
    failedPersonaCount,
    averageScores: {
      overallScore: clampAudienceScore(totals.overallScore / count),
      hookScore: clampAudienceScore(totals.hookScore / count),
      emotionScore: clampAudienceScore(totals.emotionScore / count),
      clarityScore: clampAudienceScore(totals.clarityScore / count),
    },
    dominantRevisionPriorities,
  };
};
